import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Shield, FileText, Users, Download, Plus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { NDAAcceptanceTable } from '@/components/admin/NDAAcceptanceTable';
import { NDAPendingUsers } from '@/components/admin/NDAPendingUsers';
import { NDAVersionManager } from '@/components/admin/NDAVersionManager';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function NDAManagement() {
  const { isSuperAdmin, loading } = useSuperAdmin();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('nda-export-compliance', {});

      if (error) throw error;

      // Create downloadable CSV
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nda-compliance-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Compliance report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export compliance report');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">NDA Management</h1>
            <p className="text-sm text-muted-foreground">
              Manage legal documents, track compliance, and oversee acceptances
            </p>
          </div>
        </div>
        <Button onClick={handleExport} disabled={exporting}>
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export Report'}
        </Button>
      </div>

      <Tabs defaultValue="acceptances" className="space-y-4">
        <TabsList>
          <TabsTrigger value="acceptances" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Acceptances
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Pending
          </TabsTrigger>
          <TabsTrigger value="versions" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Versions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="acceptances" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>NDA Acceptance History</CardTitle>
              <CardDescription>View all NDA acceptances across companies</CardDescription>
            </CardHeader>
            <CardContent>
              <NDAAcceptanceTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Acceptances</CardTitle>
              <CardDescription>Users who need to accept NDAs for company access</CardDescription>
            </CardHeader>
            <CardContent>
              <NDAPendingUsers />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="versions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>NDA Version Management</CardTitle>
              <CardDescription>
                Create and manage NDA versions (triggers re-acceptance flow)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <NDAVersionManager />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
